import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';
import DataTable from '../components/common/DataTable';
import FormModal from '../components/common/FormModal';
import InfoModal from '../components/common/InfoModal';
import DeleteConfirmation from '../components/common/DeleteConfirmation';
import ToastNotification from '../components/common/ToastNotification';
import UserForm from '../components/UserComponents/UserFormModal';

function UserList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await userService.getAll();
      setUsers(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setEditMode(false);
    setShowFormModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditMode(true);
    setShowFormModal(true);
  };

  const handleView = (user) => {
    setSelectedUser(user);
    setShowInfoModal(true);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

const handleFormSubmit = async (formData) => {
  setIsSubmitting(true);
  try {
    let response;
    if (editMode && selectedUser) {
      response = await userService.update(selectedUser.id, formData);
      // Check if response has success flag
      if (response && response.success === false) {
        throw new Error(response.message || 'Failed to update user');
      }
      showNotification(response?.message || 'User updated successfully');
      setShowFormModal(false);
      setSelectedUser(null);
      await fetchUsers();
    } else {
      response = await userService.create(formData);
      // Check if response has success flag
      if (response && response.success === false) {
        throw new Error(response.message || 'Failed to create user');
      }
      showNotification(response?.message || 'User created successfully');
      setShowFormModal(false);
      setSelectedUser(null);
      await fetchUsers();
    }
  } catch (err) {
    // Show error notification but DON'T close the modal
    showNotification(err.message, 'danger');
    // Re-throw the error so the form knows it failed
    throw err;
  } finally {
    setIsSubmitting(false);
  }
};

  const handleConfirmDelete = async () => {
    try {
      await userService.delete(selectedUser.id);
      showNotification('User deleted successfully');
      setShowDeleteModal(false);
      setSelectedUser(null);
      await fetchUsers();
    } catch (err) {
      showNotification(err.message, 'danger');
    }
  };

  const getRoleBadgeVariant = (role) => {
    if (!role) return 'secondary';
    switch (role.toLowerCase()) {
      case 'admin':
        return 'danger';
      case 'moderator':
      case 'editor':
        return 'warning';
      default:
        return 'info';
    }
  };

  const columns = [
    { 
      key: 'id', 
      header: 'ID',
      render: (value) => <span style={{ fontWeight: '500' }}>#{value}</span>
    },
    { 
      key: 'username', 
      header: 'Username',
      render: (value, item) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {item.profilePicture ? (
            <img 
              src={`data:image/jpeg;base64,${item.profilePicture}`} 
              alt={value}
              style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
            />
          ) : (
            <div style={{ 
              width: '32px', 
              height: '32px', 
              borderRadius: '50%', 
              background: '#e0e7ff', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontWeight: 'bold',
              color: '#4f46e5'
            }}>
              {value?.charAt(0).toUpperCase()}
            </div>
          )}
          <span style={{ fontWeight: '500' }}>{value}</span>
        </div>
      )
    },
    { 
      key: 'email', 
      header: 'Email',
      render: (value) => <a href={`mailto:${value}`} style={{ textDecoration: 'none' }}>{value}</a>
    },
    { 
      key: 'firstName', 
      header: 'Full Name',
      render: (value, item) => {
        const firstName = item.firstName || '';
        const lastName = item.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || 'N/A';
      }
    },
    { 
      key: 'userRole', 
      header: 'Role',
      render: (value) => (
        <span className={`admin-crud-badge admin-crud-badge-${getRoleBadgeVariant(value)}`}>
          {value || 'User'}
        </span>
      )
    },
    { 
      key: 'createdAt', 
      header: 'Joined',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    }
  ];

  const infoFields = [
    { key: 'id', label: 'User ID' },
    { key: 'username', label: 'Username' },
    { key: 'email', label: 'Email' },
    { 
      key: 'firstName', 
      label: 'First Name', 
      render: (value) => value || 'N/A' 
    },
    { 
      key: 'lastName', 
      label: 'Last Name', 
      render: (value) => value || 'N/A' 
    },
    { 
      key: 'fullName', 
      label: 'Full Name',
      render: (value, item) => {
        const firstName = item?.firstName || '';
        const lastName = item?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();
        return fullName || 'N/A';
      }
    },
    { 
      key: 'dateOfBirth', 
      label: 'Date of Birth',
      render: (value) => value ? new Date(value).toLocaleDateString() : 'N/A'
    },
    { 
      key: 'userRole', 
      label: 'Role',
      render: (value) => value || 'User',
      badge: (value) => {
        if (!value) return 'info';
        switch(value.toLowerCase()) {
          case 'admin': return 'danger';
          case 'moderator': return 'warning';
          default: return 'info';
        }
      }
    },
    { 
      key: 'bio', 
      label: 'Bio',
      render: (value) => value || 'No bio provided'
    },
    { 
      key: 'profilePicture', 
      label: 'Profile Picture',
      render: (value) => value ? (
        <img 
          src={`data:image/jpeg;base64,${value}`} 
          alt="Profile" 
          style={{ maxWidth: '150px', borderRadius: '8px' }}
        />
      ) : 'No profile picture'
    },
    { 
      key: 'createdAt', 
      label: 'Joined Date',
      render: (value) => value ? new Date(value).toLocaleString() : 'N/A'
    },
    { 
      key: 'updatedAt', 
      label: 'Last Updated',
      render: (value) => value ? new Date(value).toLocaleString() : 'N/A'
    }
  ];

  if (loading) {
    return (
      <div className="admin-crud-wrapper">
        <div className="admin-crud-loader">
          <div className="admin-crud-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-crud-wrapper">
      <div className="admin-crud-container">
        {error && (
          <div className="admin-crud-toast" style={{ position: 'relative', marginBottom: '20px' }}>
            <div className="admin-crud-toast-content admin-crud-toast-danger">
              <div className="admin-crud-toast-header">
                <strong>Error</strong>
                <button onClick={() => setError(null)}>✕</button>
              </div>
              <div className="admin-crud-toast-body">{error}</div>
            </div>
          </div>
        )}

        <DataTable
          title="Users Management"
          columns={columns}
          data={users}
          onAdd={handleAdd}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          addButtonText="Create New User"
          searchPlaceholder="Search users by username, email or role..."
        />

        {/* Form Modal */}
        <FormModal
          show={showFormModal}
          onHide={() => {
            setShowFormModal(false);
            setSelectedUser(null);
            setEditMode(false);
          }}
          title={editMode ? 'Edit User' : 'Create New User'}
          size="lg"
        >
          <UserForm
            initialData={selectedUser}
            onSubmit={handleFormSubmit}
            onCancel={() => {
              setShowFormModal(false);
              setSelectedUser(null);
              setEditMode(false);
            }}
            isSubmitting={isSubmitting}
            mode={editMode ? 'edit' : 'create'}
          />
        </FormModal>

        {/* Info Modal */}
        <InfoModal
          show={showInfoModal}
          onHide={() => {
            setShowInfoModal(false);
            setSelectedUser(null);
          }}
          title="User Details"
          data={selectedUser}
          fields={infoFields}
        />

        {/* Delete Confirmation Modal */}
        <DeleteConfirmation
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setSelectedUser(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete User"
          message={`Are you sure you want to delete user "${selectedUser?.username}"? This action cannot be undone.`}
        />

        {/* Toast Notification */}
        <ToastNotification
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastType}
        />
      </div>
    </div>
  );
}

export default UserList;